import { useState } from "react";
import {
  AppBar,
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import { Link, NavLink, Outlet, useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";

import logo from "../assets/logos/PROCare_logo.svg";
import mark from "../assets/logos/PC_logo.svg";
import { AccountMenu } from "../components/AccountMenu";
import { queryKeys } from "../constants/queryKeys";
import { getHospital } from "../features/settings/services/hospitalService";
import { useSession } from "../features/auth/useSession";
import { can } from "../lib/permissions";

const EXPANDED_WIDTH = 240;
const MINI_WIDTH = 72;

/**
 * Three responsive bands (see apps/dashboard/CLAUDE.md):
 *   >= lg   permanent expanded drawer
 *   md–lg   permanent mini drawer, icons + tooltips  (tablet)
 *   < md    temporary drawer behind a hamburger      (phone)
 */
export function AppShell() {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));
  const isPhone = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const { t } = useTranslation();
  const location = useLocation();

  const session = useSession();

  const items = [
    { to: "/", label: t("nav.home"), icon: <HomeOutlinedIcon />, end: true },
    { to: "/patients", label: t("nav.patients"), icon: <PeopleOutlineIcon /> },
    { to: "/studies", label: t("nav.studies"), icon: <ScienceOutlinedIcon /> },
    {
      to: "/surveys",
      label: t("nav.surveys"),
      icon: <AssignmentOutlinedIcon />,
    },
    // Gated as UX, not as a guard: the user list is readable by anyone
    // authenticated, but a nav entry whose page is all refused buttons reads
    // as broken rather than as restricted.
    ...(can(session.user, "MANAGE_USERS")
      ? [{ to: "/users", label: t("nav.users"), icon: <GroupOutlinedIcon /> }]
      : []),
    // Gated as UX, not as a guard: the route is capability-checked server
    // side, and an entry leading to a page of refusals reads as broken.
    ...(can(session.user, "VIEW_AUDIT_LOG")
      ? [
          {
            to: "/settings/audit",
            label: t("nav.auditLog"),
            icon: <FactCheckOutlinedIcon />,
          },
        ]
      : []),
    ...(can(session.user, "VIEW_ACCESS_LOG")
      ? [
          {
            to: "/settings/access-log",
            label: t("nav.accessLog"),
            icon: <HistoryOutlinedIcon />,
          },
        ]
      : []),
    {
      to: "/settings/hospital",
      label: t("nav.settings"),
      icon: <SettingsOutlinedIcon />,
    },
  ];

  const expanded = isDesktop || (isPhone && mobileOpen);
  const width = expanded ? EXPANDED_WIDTH : MINI_WIDTH;

  /**
   * The facility's own branding, when it has uploaded one. Same query key as
   * the settings page, so switching between them costs no request and saving a
   * new logo there repaints the nav immediately.
   *
   * Failure is silent on purpose: an unseeded deployment answers 404 and a
   * clinician cannot act on that from the navigation. It falls back to the
   * ProCare logo, which is what every deployment looked like before.
   */
  const hospital = useQuery({
    queryKey: queryKeys.hospital,
    queryFn: getHospital,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const facilityLogo = hospital.data?.logo ?? null;
  const facilityName = hospital.data?.name ?? "";

  const nav = (
    <List sx={{ px: 1, py: 2 }}>
      {items.map((item) => {
        const button = (
          <ListItemButton
            key={item.to}
            component={NavLink}
            to={item.to}
            end={item.end}
            onClick={() => setMobileOpen(false)}
            sx={{
              borderRadius: 2,
              mb: 0.5,
              minHeight: 44,
              justifyContent: expanded ? "flex-start" : "center",
              "&.active": {
                bgcolor: "primary.light",
                color: "primary.dark",
                "& .MuiListItemIcon-root": { color: "primary.dark" },
              },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: expanded ? 2 : 0,
                justifyContent: "center",
              }}
            >
              {item.icon}
            </ListItemIcon>
            {expanded ? <ListItemText primary={item.label} /> : null}
          </ListItemButton>
        );

        // Collapsed rail: the label only exists as a tooltip, so it has to be
        // the accessible name too.
        return expanded ? (
          button
        ) : (
          <Tooltip key={item.to} title={item.label} placement="right">
            <span>{button}</span>
          </Tooltip>
        );
      })}
    </List>
  );

  const drawerContent = (
    <Box
      sx={{ display: "flex", flexDirection: "column", height: "100%" }}
      component="nav"
      aria-label={t("nav.home")}
    >
      <Toolbar
        sx={{
          px: expanded ? 2.5 : 1,
          gap: 1.5,
          justifyContent: "center",
        }}
      >
        {/* The WHOLE brand row is the link, divider and vendor mark included.
            One anchor rather than two (two to the same place is noise for a
            screen reader), and it is what makes the corner clickable where a
            facility logo leaves the row half taken by something else: an
            anchor wrapping only the facility image is a target the size of
            that image, and the gap beside it did nothing. `nav.home` is the
            accessible name, so the alt text inside is not announced twice. */}
        <Box
          component={Link}
          to="/"
          onClick={() => setMobileOpen(false)}
          aria-label={t("nav.home")}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexGrow: 1,
            minWidth: 0,
            maxWidth: "100%",
            justifyContent: "center",
            alignSelf: "stretch",
            borderRadius: 1,
          }}
        >
          <Box
            component="img"
            src={facilityLogo?.dataUrl ?? (expanded ? logo : mark)}
            alt={facilityLogo ? facilityName : "ProCare"}
            sx={{ height: 28, maxWidth: "100%", objectFit: "contain" }}
          />
          {/* The facility comes first and the vendor keeps the square mark:
              two wordmarks side by side do not fit 240px, and the rail has
              room for neither. */}
          {facilityLogo && expanded ? (
            <>
              <Divider orientation="vertical" flexItem sx={{ my: 2 }} />
              <Box
                component="img"
                src={mark}
                alt="ProCare"
                sx={{ height: 22, flexShrink: 0, opacity: 0.75 }}
              />
            </>
          ) : null}
        </Box>
      </Toolbar>
      <Divider />
      {nav}
      <Box sx={{ flexGrow: 1 }} />
      <Divider />
      <AccountMenu compact={!expanded} />
    </Box>
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100dvh" }}>
      {isPhone ? (
        <>
          <AppBar
            position="fixed"
            color="inherit"
            elevation={0}
            sx={{ borderBottom: 1, borderColor: "divider" }}
          >
            <Toolbar>
              <IconButton
                edge="start"
                onClick={() => setMobileOpen(true)}
                aria-label={t("nav.openMenu")}
              >
                <MenuIcon />
              </IconButton>
              <Box
                component={Link}
                to="/"
                aria-label={t("nav.home")}
                sx={{ display: "flex", ml: 1, borderRadius: 1 }}
              >
                <Box
                  component="img"
                  src={facilityLogo?.dataUrl ?? logo}
                  alt={facilityLogo ? facilityName : "ProCare"}
                  sx={{ height: 24, maxWidth: 180, objectFit: "contain" }}
                />
              </Box>
              {/* One bar, 390px wide, already carrying a hamburger: the vendor
                  mark rides on the right rather than crowding the facility. */}
              {facilityLogo ? (
                <Box
                  component="img"
                  src={mark}
                  alt="ProCare"
                  sx={{ height: 20, ml: "auto", opacity: 0.75 }}
                />
              ) : null}
            </Toolbar>
          </AppBar>
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
            ModalProps={{ keepMounted: true }}
            sx={{
              "& .MuiDrawer-paper": {
                width: EXPANDED_WIDTH,
                boxSizing: "border-box",
              },
            }}
          >
            {drawerContent}
          </Drawer>
        </>
      ) : (
        <Drawer
          variant="permanent"
          sx={{
            width,
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              width,
              boxSizing: "border-box",
              borderRight: 1,
              borderColor: "divider",
              transition: theme.transitions.create("width", {
                duration: theme.transitions.duration.shorter,
              }),
              overflowX: "hidden",
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      <Box
        component="main"
        // Focus target for skip-to-content and for post-navigation focus.
        key={location.pathname}
        sx={{
          flexGrow: 1,
          minWidth: 0,
          px: { xs: 2, sm: 3, lg: 4 },
          pt: { xs: 10, md: 4 },
          pb: 6,
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
}

export function PageTitle({ children }: { children: string }) {
  return (
    <Typography variant="h1" sx={{ mb: 3 }}>
      {children}
    </Typography>
  );
}
